import { useState } from "react";

import { type ParsedPantryView, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { type CategoryKey, type PantryCopy, type UnitKey, pantryCopy } from "../lib/i18n/pantry";
import { ScreenShell } from "./screen-shell";
import { AiFooter, Conf, H1, MonoMeta, TopBar, confLevelFromScore } from "./ui/primitives";

type Props = {
  householdId: string;
  parsed: ParsedPantryView;
  onSaved: () => void;
  onCancel: () => void;
};

type Item = ParsedPantryView["items"][number];

// Localizes a stored unit/category to the active language; unknown values
// (free-form from AI) are shown verbatim.
function unitLabel(unit: string, t: PantryCopy): string {
  return unit in t.unitLabel ? t.unitLabel[unit as UnitKey] : unit;
}

function categoryLabel(category: string, t: PantryCopy): string {
  return category in t.categoryLabel ? t.categoryLabel[category as CategoryKey] : category;
}

function formatQty(item: Item, t: PantryCopy): string {
  if (item.quantity === null && !item.unit) return t.confirmQtyUnknown;
  if (item.quantity === null) return (item.unit ? unitLabel(item.unit, t) : "").toUpperCase();
  return `${item.quantity}${item.unit ? ` ${unitLabel(item.unit, t)}` : ""}`.toUpperCase();
}

export function PantryPhotoConfirmScreen({ householdId, parsed, onSaved, onCancel }: Props) {
  const { lang } = useLang();
  const t = pantryCopy[lang];
  const [items, setItems] = useState<Item[]>(parsed.items);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function removeItem(idx: number) {
    setItems(items.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    if (items.length === 0) {
      onCancel();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.bulkCreatePantry(householdId, { items, source: "photo" });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errSave);
    } finally {
      setSubmitting(false);
    }
  }

  const countLabel = t.confirmCount(items.length);
  const ctaLabel = t.confirmCta(items.length);

  return (
    <ScreenShell hideHeader>
      <TopBar back={t.confirmBack} title={t.confirmTopTitle} onBack={onCancel} />

      <div style={{ paddingTop: 18, paddingBottom: 6 }}>
        <H1 accent=".">{countLabel}</H1>
        <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              color: "var(--ink)",
            }}
          >
            {t.confirmDetected}
          </span>
          {t.confirmCheckHint}
        </p>
      </div>

      {items.length === 0 ? (
        <p style={{ marginTop: 24, fontSize: 13, color: "var(--muted)" }}>{t.confirmEmpty}</p>
      ) : (
        <div style={{ marginTop: 18, paddingBottom: 120 }}>
          {items.map((item, idx) => {
            const level = confLevelFromScore(item.confidence);
            const num = String(idx + 1).padStart(2, "0");
            const catLabel = item.category
              ? ` · ${categoryLabel(item.category, t).toUpperCase()}`
              : "";
            return (
              <div
                key={`${item.name}-${idx}`}
                style={{
                  padding: "14px 0",
                  borderTop: idx === 0 ? "1px solid var(--nav-border)" : "none",
                  borderBottom: "1px solid var(--nav-border)",
                }}
              >
                <div className="flex items-start gap-3.5">
                  <div
                    style={{
                      flex: "0 0 22px",
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      color: "var(--muted)",
                      fontWeight: 700,
                      paddingTop: 3,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 4 }}>
                      <span
                        className="truncate"
                        style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}
                      >
                        {item.name}
                      </span>
                      <Conf level={level} />
                      {item.isStaple ? (
                        <span
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: "0.1em",
                            padding: "2px 6px",
                            borderRadius: 3,
                            background: "var(--surface-2)",
                            color: "var(--muted)",
                          }}
                        >
                          {t.confirmStapleTag}
                        </span>
                      ) : null}
                    </div>
                    <MonoMeta>
                      {formatQty(item, t)}
                      {catLabel}
                    </MonoMeta>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="shrink-0 transition-colors"
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--muted)",
                      padding: "4px 6px",
                    }}
                    aria-label={t.pantryDeleteAria(item.name)}
                  >
                    {t.remove}
                  </button>
                </div>
              </div>
            );
          })}

          <AiFooter>{t.confirmAiFooter}</AiFooter>
        </div>
      )}

      {error ? (
        <p
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 12 }}
        >
          {error}
        </p>
      ) : null}

      {/* sticky CTA — floating at bottom of viewport, inside ScreenShell padding */}
      <div
        className="safe-bottom"
        style={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          marginTop: "auto",
          paddingTop: 12,
          paddingBottom: 12,
          background:
            "linear-gradient(to top, var(--bg) 60%, color-mix(in srgb, var(--bg) 0%, transparent))",
        }}
      >
        <button
          type="button"
          onClick={handleSave}
          disabled={submitting || items.length === 0}
          className="w-full inline-flex items-center justify-center gap-2 uppercase transition-all disabled:opacity-50"
          style={{
            padding: "17px 20px",
            borderRadius: 12,
            background: "var(--accent)",
            color: "var(--accent-ink)",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.02em",
            boxShadow: "var(--shadow-cta)",
          }}
        >
          {submitting ? t.saving : ctaLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="w-full text-[13px] font-medium"
          style={{ color: "var(--ink-2)", marginTop: 8 }}
        >
          {t.cancel}
        </button>
      </div>
    </ScreenShell>
  );
}
